import { formatLocalDate } from '@/utils/localDateUtils';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Pago, TipoPago } from '@/types';
import paymentService from '@/services/paymentService';
import DataTable, { Column } from '@/components/common/DataTable';
import MainLayout from '@/components/layout/MainLayout';
import { getPaymentStatusBadge } from '@/utils/paymentStatus';

export default function PaymentList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const estadoFilter = searchParams.get('estado') || '';
  const [result, setResult] = useState<{ items: Pago[]; total: number; page: number; totalPages: number }>({
    items: [],
    total: 0,
    page: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const skipSearchEffect = useRef(true);

  const load = async (
    p = 1,
    searchTerm = search,
    estadoTerm: string | null = estadoFilter,
    sortOverride?: { column: string | null; direction: 'asc' | 'desc' }
  ) => {
    setLoading(true);
    try {
      const estado = estadoTerm || undefined;
      const q = searchTerm.trim() || undefined;
      const activeSortColumn = sortOverride?.column ?? sortColumn;
      const activeSortDirection = sortOverride?.direction ?? sortDirection;
      const res: any = await paymentService.getPayments({
        page: p,
        limit: 25,
        ...(estado ? { estado } : {}),
        ...(q ? { q } : {}),
        ...(activeSortColumn ? { sort: activeSortColumn, dir: activeSortDirection } : {}),
      });
      const payload = res?.success && res.data ? res.data : res;
      const nextResult = {
        items: Array.isArray(payload?.items) ? payload.items : [],
        total: Number(payload?.total ?? 0),
        page: Number(payload?.page ?? p),
        totalPages: Number(payload?.totalPages ?? 1),
      };
      setResult(nextResult);
      setPage(nextResult.page);
    } catch (error) {
      console.error('Error fetching payments:', error);
      setResult({ items: [], total: 0, page: p, totalPages: 1 });
      setPage(p);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (skipSearchEffect.current) {
      skipSearchEffect.current = false;
      return;
    }

    const timeout = window.setTimeout(() => load(1, search, estadoFilter), 300);
    return () => window.clearTimeout(timeout);
  }, [search, estadoFilter]);

  useEffect(() => {
    load(1, search, estadoFilter);
  }, [estadoFilter, sortColumn, sortDirection]);

  const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortDirection(direction);
    load(1, search, estadoFilter, { column, direction });
  };

  const columns: Column<Pago>[] = [
    {
      key: 'contrato_id',
      label: 'Contrato',
      render: (payment) => payment.contrato?.numero_contrato || `#${payment.contrato_id}`
    },
    {
      key: 'tipo',
      label: 'Tipo',
      sortable: true,
      render: (payment) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          payment.tipo === TipoPago.INQUILINO ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
        }`}>
          {payment.tipo}
        </span>
      )
    },
    {
      key: 'fecha_limite',
      label: 'Vencimiento',
      sortable: true,
      render: (payment) => formatLocalDate(payment.fecha_limite)
    },
    {
      key: 'monto',
      label: 'Monto',
      sortable: true,
      render: (payment) => `${payment.moneda} ${Number(payment.monto).toLocaleString()}`
    },
    {
      key: 'fecha_pago',
      label: 'Fecha Pago',
      className: 'hidden 2xl:table-cell',
      render: (payment) => payment.fecha_pago ? formatLocalDate(payment.fecha_pago) : '-'
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      render: (payment) => {
        const badge = getPaymentStatusBadge(payment);
        return badge ? (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full border text-xs font-medium ${badge.className}`}>
            {badge.label}
          </span>
        ) : null;
      }
    }
  ];

  const handleRowClick = (payment: Pago) => {
    navigate(`/payments/${payment.id}`);
  };

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Pagos</h1>
            <p className="text-gray-600 mt-1">Gestión de pagos y cobros</p>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={result.items}
          loading={loading}
          searchable={true}
          serverSearch={true}
          serverSort={true}
          searchValue={search}
          onSearchChange={setSearch}
          sortValue={{ column: sortColumn, direction: sortDirection }}
          onSortChange={handleSortChange}
          searchPlaceholder="Buscar por persona, dirección, contrato o identificador..."
          onRowClick={handleRowClick}
          rowKey="id"
          emptyState={
            <div className="text-center py-12">
              <div className="text-6xl mb-4">💰</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pagos registrados</h3>
              <p className="text-gray-600">Los pagos se generan automáticamente desde los contratos</p>
            </div>
          }
        />

        {result.items.length > 0 && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
            <div className="text-sm text-gray-600">
              Mostrando {((page - 1) * 25) + 1} a {Math.min(page * 25, result.total)} de {result.total} pagos
            </div>
            <div className="flex gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page <= 1}
                onClick={() => load(page - 1, search, estadoFilter)}
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-gray-700">
                Página {page} de {result.totalPages}
              </span>
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page >= result.totalPages}
                onClick={() => load(page + 1, search, estadoFilter)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
